
import React from "react";
import {
    Box,
    Typography,
    Stack,
    Link,
    Divider,
    Button,
    Container,
    Grid,
} from "@mui/material";

import SportsEsportsIcon from "@mui/icons-material/SportsEsports";
import FacebookIcon from "@mui/icons-material/Facebook";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import EmojiEventsRoundedIcon from "@mui/icons-material/EmojiEventsRounded";
import ShieldRoundedIcon from "@mui/icons-material/ShieldRounded";

const Footer = () => {
    const footerGroups = [
        {
            title: "DISCOVER",
            links: [
                { label: "Home", href: "/" },
                { label: "Download Client", href: "/downloads" },
                { label: "Player Rankings", href: "/ranks" },
                { label: "Battle Schedule", href: "/schedules" },
            ],
        },
        {
            title: "COMMUNITY",
            links: [
                {
                    label: "Discord",
                    href: "https://discord.gg/BeQevF6eqb",
                    external: true,
                },
                {
                    label: "Facebook",
                    href: "https://www.facebook.com/profile.php?id=61594823784951",
                    external: true,
                },
                { label: "Player Guides", href: "/wiki" },
            ],
        },
        {
            title: "INFORMATION",
            links: [
                { label: "Terms of Service", href: "/terms" },
                { label: "Privacy Policy", href: "/privacy-policy" },
                { label: "Refund Policy", href: "/refund-policy" },
            ],
        },
    ];

    const features = [
        {
            icon: <PeopleAltRoundedIcon />,
            title: "UNITED GUILDS",
            description: "Build alliances. Fight as one.",
        },
        {
            icon: <EmojiEventsRoundedIcon />,
            title: "PROVE YOUR WORTH",
            description: "Every battle writes your story.",
        },
        {
            icon: <ShieldRoundedIcon />,
            title: "CLASSIC 5165",
            description: "Old-school gameplay returns.",
        },
    ];

    const goToDownloads = () => {
        window.location.href = "/downloads";
    };

    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };

    return (
        <Box
            component="footer"
            sx={{
                position: "relative",
                overflow: "hidden",
                color: "#FFFFFF",
                background:
                    "linear-gradient(145deg, #080D09 0%, #0C1910 48%, #050806 100%)",
                borderTop: "1px solid rgba(85,216,120,0.25)",

                "&:before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: "10%",
                    width: "80%",
                    height: "1px",
                    background:
                        "linear-gradient(90deg, transparent, #55D878, transparent)",
                },
            }}
        >
            {/* BACKGROUND DECORATION */}
            <Box
                sx={{
                    position: "absolute",
                    top: -180,
                    left: -120,
                    width: 350,
                    height: 350,
                    borderRadius: "50%",
                    background: "rgba(50,190,91,0.08)",
                    filter: "blur(100px)",
                    pointerEvents: "none",
                }}
            />

            <Box
                sx={{
                    position: "absolute",
                    right: -150,
                    top: 250,
                    width: 400,
                    height: 400,
                    borderRadius: "50%",
                    background: "rgba(32,110,57,0.09)",
                    filter: "blur(120px)",
                    pointerEvents: "none",
                }}
            />

            <Container
                maxWidth="xl"
                sx={{
                    position: "relative",
                    zIndex: 1,
                    pt: { xs: 5, md: 8 },
                    pb: 3,
                }}
            >
                {/* =====================================
                    ADVENTURE SECTION
                ====================================== */}
                <Box
                    sx={{
                        position: "relative",
                        display: "flex",
                        flexDirection: { xs: "column", md: "row" },
                        justifyContent: "space-between",
                        alignItems: { xs: "stretch", md: "center" },
                        gap: 5,
                        p: { xs: 3, sm: 4, md: 6 },
                        mb: { xs: 7, md: 10 },
                        overflow: "hidden",
                        borderRadius: { xs: "18px", md: "26px" },
                        background:
                            "linear-gradient(120deg, #10271A 0%, #0B1710 55%, #102519 100%)",
                        border: "1px solid rgba(85,216,120,0.22)",
                        boxShadow: "0 25px 70px rgba(0,0,0,0.3)",

                        "&:before": {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "42%",
                            height: "3px",
                            background:
                                "linear-gradient(90deg, #55D878, transparent)",
                        },

                        "&:after": {
                            content: '""',
                            position: "absolute",
                            right: -100,
                            bottom: -220,
                            width: 400,
                            height: 400,
                            borderRadius: "50%",
                            border: "1px solid rgba(85,216,120,0.10)",
                            boxShadow:
                                "0 0 0 35px rgba(85,216,120,0.025), 0 0 0 70px rgba(85,216,120,0.015)",
                            pointerEvents: "none",
                        },
                    }}
                >
                    {/* LEFT CONTENT */}
                    <Box sx={{ position: "relative", zIndex: 1, flex: 1 }}>
                        <Typography
                            sx={{
                                color: "#55D878",
                                fontSize: "10px",
                                fontWeight: 900,
                                letterSpacing: "3px",
                                mb: 2,
                            }}
                        >
                            YOUR JOURNEY. YOUR LEGACY.
                        </Typography>

                        <Typography
                            component="h2"
                            sx={{
                                fontSize: {
                                    xs: "2.1rem",
                                    sm: "2.8rem",
                                    md: "3.5rem",
                                },
                                fontWeight: 900,
                                lineHeight: 1.08,
                                letterSpacing: "-1.5px",
                                maxWidth: 650,
                            }}
                        >
                            THE NEXT LEGEND
                            <Box
                                component="span"
                                sx={{
                                    display: "block",
                                    color: "#55D878",
                                }}
                            >
                                COULD BE YOU.
                            </Box>
                        </Typography>

                        <Typography
                            sx={{
                                mt: 2.5,
                                mb: 3.5,
                                color: "#A1ADA4",
                                fontSize: "14px",
                                lineHeight: 1.9,
                                maxWidth: 490,
                            }}
                        >
                            Enter a world of rivalries, powerful guilds,
                            and unforgettable battles. Train your character,
                            challenge your rivals, and create a legacy
                            that belongs to you.
                        </Typography>

                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1.5}
                        >
                            <Button
                                variant="contained"
                                startIcon={<PlayArrowRoundedIcon />}
                                onClick={goToDownloads}
                                sx={{
                                    py: 1.5,
                                    px: 3,
                                    borderRadius: "10px",
                                    background:
                                        "linear-gradient(135deg, #72EA91, #35B963)",
                                    color: "#07130A",
                                    fontWeight: 800,
                                    textTransform: "none",
                                    boxShadow:
                                        "0 8px 25px rgba(50,190,91,0.15)",
                                    "&:hover": {
                                        background:
                                            "linear-gradient(135deg, #B5FF9A, #55D878)",
                                        transform: "translateY(-3px)",
                                    },
                                    transition: "all 0.25s ease",
                                }}
                            >
                                Begin Your Journey
                            </Button>

                            <Button
                                variant="outlined"
                                startIcon={<DownloadRoundedIcon />}
                                onClick={goToDownloads}
                                sx={{
                                    py: 1.5,
                                    px: 3,
                                    borderRadius: "10px",
                                    borderColor:
                                        "rgba(85,216,120,0.35)",
                                    color: "#C6F5D2",
                                    fontWeight: 700,
                                    textTransform: "none",
                                    "&:hover": {
                                        borderColor: "#55D878",
                                        background:
                                            "rgba(85,216,120,0.07)",
                                        transform: "translateY(-3px)",
                                    },
                                    transition: "all 0.25s ease",
                                }}
                            >
                                Get the Client
                            </Button>
                        </Stack>
                    </Box>

                    {/* RIGHT FEATURE PANEL */}
                    <Box
                        sx={{
                            position: "relative",
                            zIndex: 1,
                            width: { xs: "100%", md: "380px" },
                            flexShrink: 0,
                        }}
                    >
                        <Typography
                            sx={{
                                mb: 2,
                                color: "#55D878",
                                fontSize: "10px",
                                fontWeight: 900,
                                letterSpacing: "2px",
                            }}
                        >
                            WHAT AWAITS YOU
                        </Typography>

                        <Stack spacing={1.5}>
                            {features.map((item, index) => (
                                <Box
                                    key={item.title}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 2,
                                        p: 2,
                                        borderRadius: "12px",
                                        background:
                                            "rgba(255,255,255,0.025)",
                                        border:
                                            "1px solid rgba(255,255,255,0.06)",
                                        transition: "all 0.25s ease",

                                        "&:hover": {
                                            background:
                                                "rgba(85,216,120,0.06)",
                                            borderColor:
                                                "rgba(85,216,120,0.25)",
                                            transform: "translateX(4px)",
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 45,
                                            height: 45,
                                            flexShrink: 0,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            borderRadius: "12px",
                                            background:
                                                "rgba(85,216,120,0.09)",
                                            color: "#55D878",
                                        }}
                                    >
                                        {item.icon}
                                    </Box>

                                    <Box>
                                        <Typography
                                            sx={{
                                                fontSize: "11px",
                                                fontWeight: 900,
                                                letterSpacing: "0.7px",
                                                color: "#F2FFF5",
                                            }}
                                        >
                                            {item.title}
                                        </Typography>

                                        <Typography
                                            sx={{
                                                mt: 0.5,
                                                fontSize: "12px",
                                                color: "#89968D",
                                            }}
                                        >
                                            {item.description}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Stack>
                    </Box>
                </Box>

                {/* =====================================
                    FOOTER NAVIGATION
                ====================================== */}
                <Grid
                    container
                    spacing={{ xs: 4, md: 5 }}
                    sx={{ mb: { xs: 5, md: 7 } }}
                >
                    {/* BRAND */}
                    <Grid item xs={12} md={4}>
                        <Typography
                            sx={{
                                fontSize: {
                                    xs: "1.8rem",
                                    md: "2rem",
                                },
                                fontWeight: 900,
                                letterSpacing: "-1px",
                                lineHeight: 1.2,
                                mb: 1.5,
                            }}
                        >
                            CRITICAL
                            <Box
                                component="span"
                                sx={{
                                    display: "block",
                                    color: "#55D878",
                                }}
                            >
                                CONQUER
                            </Box>
                        </Typography>

                        <Box
                            sx={{
                                width: 65,
                                height: 3,
                                mb: 2.5,
                                background:
                                    "linear-gradient(90deg, #55D878, transparent)",
                            }}
                        />

                        <Typography
                            sx={{
                                color: "#89968D",
                                fontSize: "13px",
                                lineHeight: 2,
                                maxWidth: 360,
                            }}
                        >
                            A classic 5165 MMORPG experience built around
                            character progression, fierce PvP, guild
                            rivalries, and the spirit of old-school Conquer.
                            Make your mark on the battlefield.
                        </Typography>

                        <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
                            <Box
                                component="a"
                                href="https://discord.gg/BeQevF6eqb"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Discord"
                                sx={{
                                    width: 45,
                                    height: 45,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    borderRadius: "12px",
                                    color: "#55D878",
                                    background: "#0D1A11",
                                    border:
                                        "1px solid rgba(85,216,120,0.15)",
                                    textDecoration: "none",
                                    transition: "all 0.25s ease",
                                    "&:hover": {
                                        color: "#FFFFFF",
                                        background:
                                            "rgba(85,216,120,0.12)",
                                        borderColor: "#55D878",
                                        transform: "translateY(-3px)",
                                    },
                                }}
                            >
                                <SportsEsportsIcon />
                            </Box>

                            <Box
                                component="a"
                                href="https://www.facebook.com/CriticalConquer2026"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Facebook"
                                sx={{
                                    width: 45,
                                    height: 45,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    borderRadius: "12px",
                                    color: "#7CAEFF",
                                    background: "#0D1A11",
                                    border:
                                        "1px solid rgba(85,216,120,0.15)",
                                    textDecoration: "none",
                                    transition: "all 0.25s ease",
                                    "&:hover": {
                                        background:
                                            "rgba(24,119,242,0.12)",
                                        borderColor: "#4385F5",
                                        transform: "translateY(-3px)",
                                    },
                                }}
                            >
                                <FacebookIcon />
                            </Box>
                        </Stack>
                    </Grid>

                    {/* LINK COLUMNS */}
                    {footerGroups.map((group) => (
                        <Grid item xs={6} sm={4} md={2.66} key={group.title}>
                            <Typography
                                sx={{
                                    mb: 2.5,
                                    color: "#55D878",
                                    fontSize: "10px",
                                    fontWeight: 900,
                                    letterSpacing: "1.8px",
                                }}
                            >
                                {group.title}
                            </Typography>

                            <Stack spacing={1.8}>
                                {group.links.map((link) => (
                                    <Link
                                        key={link.label}
                                        href={link.href}
                                        target={
                                            link.external
                                                ? "_blank"
                                                : undefined
                                        }
                                        rel={
                                            link.external
                                                ? "noopener noreferrer"
                                                : undefined
                                        }
                                        underline="none"
                                        sx={{
                                            width: "fit-content",
                                            color: "#89968D",
                                            fontSize: "13px",
                                            transition: "all 0.2s ease",
                                            "&:hover": {
                                                color: "#55D878",
                                                transform: "translateX(4px)",
                                            },
                                        }}
                                    >
                                        {link.label}
                                    </Link>
                                ))}
                            </Stack>
                        </Grid>
                    ))}
                </Grid>

                {/* COMMUNITY STRIP */}
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: {
                            xs: "column",
                            sm: "row",
                        },
                        justifyContent: "space-between",
                        alignItems: {
                            xs: "flex-start",
                            sm: "center",
                        },
                        gap: 2.5,
                        p: { xs: 2.5, md: 3 },
                        mb: 4,
                        borderRadius: "14px",
                        background:
                            "linear-gradient(100deg, rgba(85,216,120,0.07), rgba(255,255,255,0.015))",
                        border:
                            "1px solid rgba(85,216,120,0.13)",
                    }}
                >
                    <Box>
                        <Typography
                            sx={{
                                fontSize: "13px",
                                fontWeight: 800,
                                color: "#E8F5EB",
                            }}
                        >
                            YOUR GUILD IS WAITING.
                        </Typography>

                        <Typography
                            sx={{
                                mt: 0.6,
                                color: "#89968D",
                                fontSize: "12px",
                            }}
                        >
                            Connect with the community beyond the battlefield.
                        </Typography>
                    </Box>

                    <Button
                        component="a"
                        href="https://discord.gg/BeQevF6eqb"
                        target="_blank"
                        rel="noopener noreferrer"
                        endIcon={<PlayArrowRoundedIcon />}
                        sx={{
                            px: 2.5,
                            py: 1.1,
                            borderRadius: "9px",
                            color: "#55D878",
                            border:
                                "1px solid rgba(85,216,120,0.25)",
                            textTransform: "none",
                            fontWeight: 800,
                            fontSize: "12px",
                            "&:hover": {
                                background:
                                    "rgba(85,216,120,0.08)",
                                borderColor: "#55D878",
                            },
                        }}
                    >
                        Join Discord
                    </Button>
                </Box>

                {/* =====================================
                    COPYRIGHT
                ====================================== */}
                <Divider
                    sx={{
                        borderColor: "rgba(85,216,120,0.12)",
                        mb: 3,
                    }}
                />

                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: { xs: "flex-start", sm: "center" },
                        flexDirection: { xs: "column", sm: "row" },
                        gap: 2,
                    }}
                >
                    <Box>
                        <Typography
                            sx={{
                                color: "#66746A",
                                fontSize: "11px",
                            }}
                        >
                            � {new Date().getFullYear()} Critical Conquer.
                            All rights reserved.
                        </Typography>

                        <Typography
                            sx={{
                                mt: 0.6,
                                color: "#48554B",
                                fontSize: "10px",
                            }}
                        >
                            Classic gameplay. A new chapter.
                        </Typography>
                    </Box>

                    <Typography
                        sx={{
                            color: "#66746A",
                            fontSize: "10px",
                            fontWeight: 700,
                            letterSpacing: "1.5px",
                        }}
                    >
                        BUILT FOR THE BATTLEFIELD
                    </Typography>

                    <Button
                        onClick={scrollToTop}
                        endIcon={<KeyboardArrowUpIcon />}
                        sx={{
                            color: "#55D878",
                            border:
                                "1px solid rgba(85,216,120,0.18)",
                            borderRadius: "10px",
                            px: 2,
                            py: 1,
                            fontSize: "11px",
                            fontWeight: 800,
                            textTransform: "none",
                            "&:hover": {
                                background:
                                    "rgba(85,216,120,0.08)",
                                borderColor: "#55D878",
                            },
                        }}
                    >
                        Back to top
                    </Button>
                </Box>
            </Container>
        </Box>
    );
};

export default Footer;
